import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import { Link } from "react-router-dom";

import type { IWidgetRenderContext } from "@/components/dashboard-grid";
import { cn } from "@/utils/cn";
import { useQuickActions } from "../_quick-actions/use-quick-actions";
import { WidgetEmpty } from "./primitives";

const ROW_CLASS_NAME =
  "group flex min-h-0 flex-1 items-center gap-2 rounded-sm border border-border bg-surface px-2 transition-colors";
const TILE_CLASS_NAME =
  "group flex h-full min-h-0 flex-col items-center justify-center gap-1.5 rounded-sm border border-border bg-surface px-1 text-center transition-colors";

/** How many shortcuts each size fits without the tiles turning into slivers. */
const ACTION_COUNT = { sm: 2, tall: 4, md: 4, lg: 6, xl: 6 } as const;

export const QuickActionsWidget = ({
  size,
  isEditing,
}: IWidgetRenderContext) => {
  const { t } = useTranslation(["common", "company", "nav"]);
  // The same pinned, permission-filtered list as the dashboard's top bar, so
  // customizing it there customizes this too.
  const { pinned } = useQuickActions();

  const actions = useMemo(
    () =>
      pinned.slice(
        0,
        // `size` is typed against every size in the system; this widget only
        // offers four of them, so the map has no entry for the rest.
        ACTION_COUNT[size as keyof typeof ACTION_COUNT] ?? ACTION_COUNT.sm,
      ),
    [size, pinned],
  );

  if (actions.length === 0) {
    return <WidgetEmpty message={t("company:widgets.no_actions_for_role")} />;
  }

  // Same width as `sm`, stood up: a menu of rows reads naturally in a narrow
  // column where the square-tile grid the wider sizes use would not — four
  // tiles in one skinny width is worse than four rows.
  if (size === "tall") {
    return (
      <ul className="flex h-full flex-col gap-1">
        {actions.map((entry) => {
          const Icon = entry.icon;
          const row = (
            <>
              <span className="grid size-6 shrink-0 place-items-center rounded-sm bg-primary-muted text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                <Icon size={12} aria-hidden />
              </span>
              <span className="min-w-0 flex-1 truncate text-[11px] font-semibold text-foreground">
                {t(entry.label)}
              </span>
            </>
          );

          const rowClassName = ROW_CLASS_NAME;

          return (
            <li key={entry.key} className="flex min-h-0 flex-1">
              {isEditing ? (
                <div className={rowClassName}>{row}</div>
              ) : (
                <Link
                  to={entry.to}
                  className={cn(
                    rowClassName,
                    "w-full outline-hidden hover:border-primary/40 hover:bg-primary-muted/40 focus-visible:ring-2 focus-visible:ring-ring",
                  )}
                >
                  {row}
                </Link>
              )}
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <div
      className={cn(
        "grid h-full gap-1.5",
        size === "sm" && "grid-cols-2",
        size === "md" && "grid-cols-4",
        (size === "lg" || size === "xl") && "grid-cols-3 grid-rows-2",
      )}
    >
      {actions.map((entry) => {
        const Icon = entry.icon;
        const tile = (
          <>
            <span className="grid size-7 place-items-center rounded-sm bg-primary-muted text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
              <Icon size={14} aria-hidden />
            </span>
            <span className="truncate text-[11px] font-semibold text-foreground">
              {t(entry.label)}
            </span>
          </>
        );

        const className = TILE_CLASS_NAME;

        return isEditing ? (
          <div key={entry.key} className={className}>
            {tile}
          </div>
        ) : (
          <Link
            key={entry.key}
            to={entry.to}
            className={cn(
              className,
              "outline-hidden hover:border-primary/40 hover:bg-primary-muted/40 focus-visible:ring-2 focus-visible:ring-ring",
            )}
          >
            {tile}
          </Link>
        );
      })}
    </div>
  );
};
